import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchMainCategories } from '../api/client';

/**
 * `/discussions` — picks a main category to browse/post in, or "All
 * categories" for everything combined with no category-level filter (see
 * TopicList's `mainCategoryId` prop).
 */
export function MainCategoryList(): React.JSX.Element {
  const mainCategories = useQuery({ queryKey: ['main-categories'], queryFn: fetchMainCategories });

  return (
    <section aria-labelledby="main-category-list-heading">
      <h2 id="main-category-list-heading">Discussions</h2>
      <p className="hint">Pick a main category to browse and post in.</p>

      {mainCategories.isLoading && <p role="status">Loading categories…</p>}

      {mainCategories.error && (
        <p role="alert" data-testid="main-categories-error">
          Could not load categories.
        </p>
      )}

      <ul className="category-cards">
        <li>
          <Link to="/discussions/all" className="category-card" data-testid="main-category-card-all">
            <strong>All categories</strong>
            <span className="hint">Everything, from every main category, in one list.</span>
          </Link>
        </li>
        {mainCategories.data?.map((mainCategory) => (
          <li key={mainCategory.id}>
            <Link
              to={`/discussions/${encodeURIComponent(mainCategory.id)}`}
              className="category-card"
              data-testid={`main-category-card-${mainCategory.id}`}
            >
              <strong>{mainCategory.name}</strong>
            </Link>
          </li>
        ))}
      </ul>

      {mainCategories.data && mainCategories.data.length === 0 && (
        <p className="hint">
          No main categories yet — an administrator can add one from Manage Categories.
        </p>
      )}
    </section>
  );
}
